import React, { FC, useCallback, MouseEvent } from 'react'
import moment from 'moment'
import { Card } from 'transfix-ui/components/Card'
import { Box } from 'transfix-ui/core'
import { SHIPMENT_STATUS, IShipmentDocument } from 'types'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { IFile, ILoad } from 'apps/fleet-planner/types'
import { getFpDocuments } from 'apps/fleet-planner/utils/api'
import { AttachmentListItem } from '../AttachmentListItem'
import { UploadInputButton } from '../atoms/UploadInputButton'
import { DocumentListItem, DefaultDocumentSubtitle } from './DocumentListItem'
import PodRejectionBanner from './PodRejectionBanner'

interface ILoadDetailsFileHeaderProps {
  load?: ILoad
  onUploadFiles: (files: IFile[]) => void
}

/**
 * Groups logic for displaying attachments on the Load Details page on mobile
 */
export const LoadDetailsFileHeader: FC<ILoadDetailsFileHeaderProps> = ({ load, onUploadFiles }) => {
  const { pods: fpPods = [], isExternal, podState } = load || {}
  const [firstFpPod] = fpPods || []

  const movePodWarningToTopInCarrierAppEnabled = useFeatureFlag('movePodWarningToTopInCarrierApp')

  const { deleteAttachment, isLoading, shipmentDetails } = useMarketplaceContext()
  const { status, documents: mpDocuments = [] } = shipmentDetails || {}
  const { key: statusKey } = status || {}
  const rejectedDocuments =
    mpDocuments.filter(({ key }) => key === 'pod_rejected') || ([] as IShipmentDocument[])

  const [latestPodFile] = getFpDocuments({
    documents: fpPods,
  })

  /**
   * In this case, replace is not removing any files, and instead
   * just uploading a new one which will be displayed insted of the previous file
   */
  const replaceHandler = useCallback(
    (newFiles: IFile[]) => {
      onUploadFiles(newFiles)
    },
    [onUploadFiles]
  )

  const deleteFileHandler = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (event?.currentTarget.id && load) {
        const attachmentId = parseInt(event?.currentTarget.id, 10)
        deleteAttachment(attachmentId, load)
      }
    },
    [deleteAttachment, load]
  )

  const isRejectedStatus = statusKey === SHIPMENT_STATUS.podRejected

  const [recentRejectedDocuments] = rejectedDocuments || []
  const { podRemovalReason } = recentRejectedDocuments || {}

  return (
    <Box width='100%'>
      {/**
       * External POD with valid status
       */}
      {isExternal && latestPodFile && !isRejectedStatus && (
        <Box p={1} mb={1}>
          <Card>
            <DocumentListItem
              onReplace={replaceHandler}
              document={latestPodFile}
              documentName='POD'
              podState={podState}
              getDocumentTitle={() => 'POD'}
              getDocumentSubtitle={({ createdAt = moment().format() }) => (
                <DefaultDocumentSubtitle
                  createdAt={createdAt}
                  FormattedDateTimeProps={{ format: 'timeDateAndYear' }}
                />
              )}
              isLastChild
            />
          </Card>
        </Box>
      )}
      {/**
       * External POD with 'pod_rejected' status
       */}
      {isExternal && isRejectedStatus && !movePodWarningToTopInCarrierAppEnabled && (
        <Box p={1} pb={2}>
          <Card>
            <Box mb={2}>
              <PodRejectionBanner podRemovalReason={podRemovalReason} />
            </Box>
            <UploadInputButton onUpload={replaceHandler} label='Upload POD' disabled={isLoading} />
          </Card>
        </Box>
      )}
      {/**
       * Fleet Planner POD
       */}
      {firstFpPod && !latestPodFile && (
        <Box p={2} pb={0}>
          <AttachmentListItem
            attachment={firstFpPod}
            onFileDelete={deleteFileHandler}
            getTitle={() => 'POD'}
            index={0}
          />
        </Box>
      )}
    </Box>
  )
}
